import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../context/AuthProvider'
import { api } from '../../../lib/api'
import ParishCard from '../../../components/ParishCard'
import ParishMap from '../../../components/ParishMap'
import { Button } from '../../../components/ui/button'
import { Search, MapPin, Loader2 } from 'lucide-react'

export default function DonorParish() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [userParishes, setUserParishes] = useState([])
  const [parishDetails, setParishDetails] = useState([])
  const [addingParish, setAddingParish] = useState(null)
  const [error, setError] = useState(null)

  // Fetch user's parishes and their details
  useEffect(() => {
    if (user?.id) {
      fetchUserParishes()
    }
  }, [user?.id])

  const fetchUserParishes = async () => {
    try {
      setLoading(true)
      setError(null)

      // Get user with parishes
      const userData = await api.getUser()
      const parishes = userData.parishes || []
      setUserParishes(parishes)

      // Fetch full details for each parish
      const details = await Promise.all(
        parishes.map(async (parish) => {
          try {
            const fullParish = await api.getParish(parish.parishId)
            return { ...fullParish, isPrimary: parish.isPrimary }
          } catch (err) {
            console.error(`Failed to fetch parish ${parish.parishId}:`, err)
            return null
          }
        })
      )

      setParishDetails(details.filter(Boolean))
    } catch (err) {
      console.error('Failed to fetch user parishes:', err)
      setError(err.message || 'Failed to load parishes')
    } finally {
      setLoading(false)
    }
  }

  const handleSetPrimary = async (parishId) => {
    try {
      setAddingParish(parishId)
      setError(null)

      await api.updateUserParish(user.id, parishId, true)
      await fetchUserParishes()
    } catch (err) {
      console.error('Failed to set primary parish:', err)
      setError(err.message || 'Failed to set primary parish')
    } finally {
      setAddingParish(null)
    }
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold font-display text-charcoal">My Parishes</h1>
        <Button
          variant="outline"
          onClick={() => navigate('/app/donor/parishes/search')}
        >
          <Search className="w-4 h-4 mr-2" />
          Find Parishes
        </Button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* User's Parishes */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-gold animate-spin" />
        </div>
      ) : parishDetails.length > 0 ? (
        <div>
          <h2 className="text-xl font-semibold font-display text-charcoal mb-4">
            Your Parishes ({parishDetails.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {parishDetails
              .sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0))
              .map((parish) => (
                <div key={parish.id} className="h-full">
                  <ParishCard
                    parish={parish}
                    onSetPrimary={() => handleSetPrimary(parish.id)}
                    isSettingPrimary={addingParish === parish.id}
                  />
                </div>
              ))}
          </div>
        </div>
      ) : (
        <div className="bg-white border border-surface-border rounded p-8 text-center">
          <MapPin className="w-12 h-12 text-charcoal-faint mx-auto mb-4" />
          <p className="text-charcoal-muted mb-4">
            You haven't added any parishes yet.
          </p>
          <Button
            variant="outline"
            onClick={() => navigate('/app/donor/parishes/search')}
          >
            <Search className="w-4 h-4 mr-2" />
            Find Your Parish
          </Button>
        </div>
      )}

      {/* Map Section */}
      <div className="mt-12">
        <h2 className="text-xl font-semibold font-display text-charcoal mb-4">
          Nearby Parishes
        </h2>
        <ParishMap
          onParishClick={(parish) => {
            navigate(`/app/donor/parish/${parish.id}`)
          }}
        />
      </div>
    </div>
  )
}
